import { useEffect, useState } from "react";
import scannerAsset from "@/assets/gatorguard-laptop-phone.png.asset.json";
import roiCalculatorAsset from "@/assets/pharmacy-valuation-imac.png.asset.json";
import heroLaptopVideoAsset from "@/assets/hero-laptop.webm.asset.json";
import heroLaptopPosterAsset from "@/assets/hero-laptop-poster.jpg.asset.json";
import { LaptopVideo } from "@/components/landing/LaptopVideo";

type HeroSlide =
  | { kind: "image"; src: string; alt: string }
  | { kind: "laptop-video"; src: string; poster: string; alt: string };

const HERO_SLIDES: HeroSlide[] = [
  {
    kind: "image",
    src: roiCalculatorAsset.url,
    alt: "Pharmacy CBS valuation calculator displayed on an iMac",
  },
  {
    kind: "laptop-video",
    src: heroLaptopVideoAsset.url,
    poster: heroLaptopPosterAsset.url,
    alt: "Campaign Framework Builder screen recording on a laptop",
  },
  {
    kind: "image",
    src: scannerAsset.url,
    alt: "GatorGuard concrete coating visualizer shown on a laptop and phone",
  },
];

export function Hero() {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setActiveIndex((i) => (i + 1) % HERO_SLIDES.length);
    }, 5000);
    return () => clearTimeout(timeout);
  }, [activeIndex]);

  return (
    <section id="hero" className="px-5 pb-12 pt-8 sm:px-6 sm:pb-16 sm:pt-10 md:px-8 md:pb-20 md:pt-16 lg:px-12">
      <div className="max-w-6xl mx-auto">
        <div className="grid gap-8 sm:gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16 lg:items-center">
          <div className="flex min-w-0 flex-col gap-5 sm:gap-6">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-primary">
              Interactive lead generation tools
            </p>
            <h1 className="text-[2rem] font-semibold leading-[1.08] tracking-normal text-balance sm:text-5xl lg:text-[3.25rem]">
              Build Something Your <span className="text-primary">Prospects</span>
              <br className="hidden sm:block" /> Actually Want to <span className="text-primary">Use</span>.
            </h1>
            <span
              className="h-1 w-40 rounded bg-gradient-to-r from-primary via-sky-300 to-transparent"
              aria-hidden="true"
            />
            <p className="max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
              Custom calculators, assessments and interactive tools that deliver real value, generate
              qualified leads and start better sales conversations.
            </p>
          </div>

          <div className="relative mx-auto w-full max-w-[34rem] lg:col-start-2 lg:row-start-1 lg:row-span-2 lg:max-w-none">
            <span
              className="pointer-events-none absolute inset-8 rounded-full bg-primary/15 blur-3xl"
              aria-hidden="true"
            />
            <div className="relative aspect-[4/3] w-full">
              {HERO_SLIDES.map((slide, index) => {
                const frameClass = `absolute inset-0 w-full h-full transition-all duration-[1400ms] ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none ${
                  index === activeIndex
                    ? "opacity-100 scale-100 translate-y-0 rotate-0"
                    : "opacity-0 scale-[0.92] translate-y-8 rotate-[-4deg] blur-[2px]"
                }`;
                if (slide.kind === "laptop-video") {
                  return (
                    <div
                      key={slide.src}
                      aria-hidden={index !== activeIndex}
                      className={`${frameClass} flex items-center justify-center px-2`}
                    >
                      <LaptopVideo
                        src={slide.src}
                        poster={slide.poster}
                        ariaLabel={slide.alt}
                        className="w-[106%] -translate-x-[3%]"
                      />
                    </div>
                  );
                }
                return (
                  <img
                    key={slide.src}
                    src={slide.src}
                    alt={slide.alt}
                    width={1000}
                    height={750}
                    className={`${frameClass} object-contain`}
                    loading={index === 0 ? "eager" : "lazy"}
                    {...(index === 0 ? { fetchPriority: "high" as const } : {})}
                    decoding={index === 0 ? "sync" : "async"}
                  />
                );
              })}
            </div>
          </div>

          <div className="flex flex-col gap-4 sm:gap-5">
            <div className="flex flex-col items-stretch gap-3 pt-1 sm:flex-row sm:items-center">
              <a
                href="#lead-generation-tools"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/30 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/10 w-full sm:w-auto"
              >
                Explore Lead Generation Tools
              </a>
              <a
                href="https://calendly.com/cgould-mn/new-meeting"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/20 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/10 w-full sm:w-auto"
              >
                Let's Talk
              </a>
            </div>
            <p className="text-center text-sm text-muted-foreground sm:text-left">
              Not sure what to build?{" "}
              <a
                href="https://calendly.com/cgould-mn/new-meeting"
                target="_blank"
                rel="noreferrer"
                className="text-primary hover:underline"
              >
                Schedule a call
              </a>
            </p>
          </div>
        </div>
      </div>
    </section>
  );

}
